import { useState } from "react";
import type { FeedItem } from "@scrile/contracts";
import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { t } from "../i18n";
import type { Outgoing } from "./sendQueue";
import type { TimelineEntry } from "./timeline";
import type { TypingKind } from "./typing";
import { VoicePlayer } from "./VoicePlayer";

/**
 * Пузыри переписки. Один компонент на строку ленты: вид пузыря — по виду элемента
 * (текст, голосовое, фото, видео, карточка героя, напоминание), сторона — герой или я.
 */

type MessageEntry = Extract<TimelineEntry, { type: "message" }>;

export interface BubbleHandlers {
  onOpenPhoto(url: string, trigger: HTMLElement): void;
  onRetry(id: string): void;
  onDiscard(id: string): void;
  /** Перед началом звука голосового — остановить остальной звук сайта. */
  onPlay(): void;
  onMediaError(): void;
}

interface HeroFace {
  name: string;
  portraitUrl: string | null;
}

export function MessageRow({ entry, hero, handlers }: { entry: MessageEntry; hero: HeroFace; handlers: BubbleHandlers }) {
  const { side, first, last } = entry;
  const classes = ["msg", `msg--${side}`, first ? "msg--first" : "", last ? "msg--last" : ""].filter(Boolean).join(" ");
  const local = entry.message.source === "local" ? entry.message.outgoing : null;
  return (
    <li className={classes}>
      {side === "hero" && (
        <span className="msg__avatar" aria-hidden="true">
          {last && <Avatar src={hero.portraitUrl} name={hero.name} size={30} />}
        </span>
      )}
      <div className="msg__column">
        {side === "hero" && first && <span className="sr-only">{hero.name}: </span>}
        {side === "me" && first && <span className="sr-only">{t("messenger.you")}: </span>}
        {entry.message.source === "feed" ? (
          <FeedBubble item={entry.message.item} time={entry.time} hero={hero} handlers={handlers} />
        ) : (
          <LocalBubble outgoing={entry.message.outgoing} time={entry.time} handlers={handlers} />
        )}
        {local?.status === "failed" && <FailedLine outgoing={local} handlers={handlers} />}
      </div>
    </li>
  );
}

function Meta({ time, status }: { time: string; status?: "sending" | "sent" | "failed" | null }) {
  return (
    <span className="msg__meta">
      <time>{time}</time>
      {status === "sending" && (
        <span className="msg__status" title={t("messenger.status.sending")}>
          <Icon name="clock" size={13} />
          <span className="sr-only">{t("messenger.status.sending")}</span>
        </span>
      )}
      {status === "sent" && (
        <span className="msg__status" title={t("messenger.status.sent")}>
          <Icon name="check" size={13} />
          <span className="sr-only">{t("messenger.status.sent")}</span>
        </span>
      )}
      {status === "failed" && (
        <span className="msg__status msg__status--failed">
          <Icon name="alert" size={13} />
          <span className="sr-only">{t("messenger.status.failed")}</span>
        </span>
      )}
    </span>
  );
}

function FeedBubble({ item, time, hero, handlers }: { item: FeedItem; time: string; hero: HeroFace; handlers: BubbleHandlers }) {
  const mine = item.from === "user";
  const status = mine ? "sent" : null;
  switch (item.kind) {
    case "text":
      return (
        <div className="bubble-box bubble-box--text">
          <p className="bubble-box__text">{item.body.text}</p>
          <Meta time={time} status={status} />
        </div>
      );
    case "system":
      // Напоминание — реплика героя, только отмеченная для экранного диктора
      return (
        <div className="bubble-box bubble-box--text">
          <span className="sr-only">{t("messenger.reminder")}: </span>
          <p className="bubble-box__text">{item.body.text?.trim() || t("messenger.busy", { hero: hero.name })}</p>
          <Meta time={time} />
        </div>
      );
    case "voice":
      return <VoiceBubble item={item} time={time} status={status} hero={hero} onPlay={handlers.onPlay} onMediaError={handlers.onMediaError} />;
    case "photo":
      return <PhotoBubble key={item.body.url} url={item.body.url ?? null} time={time} hero={hero} handlers={handlers} />;
    case "video":
      return (
        <div className="bubble-box bubble-box--media">
          {item.body.url ? (
            // Видео — на месте и без звука сам по себе: не играет, пока не нажали
            <video key={item.body.url} className="bubble-box__video" src={item.body.url} controls playsInline preload="metadata" aria-label={t("messenger.announce.video", { hero: hero.name })} onPlay={handlers.onPlay} onError={handlers.onMediaError} />
          ) : (
            <p className="bubble-box__missing">{t("messenger.video.missing")}</p>
          )}
          <Meta time={time} />
        </div>
      );
    case "hero_card":
      // Карточка — контакт: кто это и ссылка на его сайт (в новой вкладке — переписка
      // остаётся открытой). Переход по ней — касание привязки (?ref=hero:<кто прислал>)
      return (
        <div className="bubble-box bubble-box--card">
          <a
            className="hero-contact"
            href={item.body.url}
            target="_blank"
            rel="noopener"
            aria-label={`${t("messenger.card.label", { hero: hero.name })}: ${item.body.name}`}
          >
            <span className="hero-contact__who">
              <Avatar src={item.body.portraitUrl} name={item.body.name} size={48} />
              <span className="hero-contact__text">
                <span className="hero-contact__name">{item.body.name}</span>
                {item.body.tagline && <span className="hero-contact__tagline">{item.body.tagline}</span>}
              </span>
            </span>
            <span className="hero-contact__open">
              {t("messenger.card.open")}
              <Icon name="external" size={15} />
            </span>
          </a>
          <span className="hero-contact__meta">
            <Meta time={time} />
          </span>
        </div>
      );
    default:
      return null;
  }
}

function VoiceBubble({
  item,
  time,
  status,
  hero,
  onPlay,
  onMediaError,
}: {
  item: Extract<FeedItem, { kind: "voice" }>;
  time: string;
  status: "sent" | null;
  hero: HeroFace;
  onPlay: () => void;
  onMediaError: () => void;
}) {
  const mine = item.from === "user";
  const transcript = item.body.text?.trim() ?? "";
  const url = item.body.url ?? null;
  const [open, setOpen] = useState(false);
  return (
    <div className="bubble-box bubble-box--voice">
      <span className="sr-only">{t("messenger.voice.label")}. </span>
      {url ? (
        <VoicePlayer key={url} url={url} seed={item.body.key ?? `seq-${item.seq}`} mine={mine} onPlay={onPlay} onMediaError={onMediaError} />
      ) : (
        <p className="bubble-box__note">
          <Icon name="voice" size={16} />
          {mine ? t("messenger.voice.label") : t("messenger.voice.noAudio", { hero: hero.name })}
        </p>
      )}
      {transcript && (url ? open : true) && <p className="bubble-box__transcript">{transcript}</p>}
      <div className="bubble-box__foot">
        {transcript && url && (
          <button type="button" className="bubble-box__toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? t("messenger.voice.hideTranscript") : t("messenger.voice.showTranscript")}
          </button>
        )}
        <Meta time={time} status={status} />
      </div>
    </div>
  );
}

/** Пропорции превью: от 3:4 до 16:9 — совсем узкое или широкое фото обрезается, целиком — на весь экран. */
export function previewRatio(width: number, height: number): number {
  if (!(width > 0) || !(height > 0)) return 4 / 3;
  return Math.min(16 / 9, Math.max(3 / 4, width / height));
}

function PhotoBubble({ url, time, hero, handlers }: { url: string | null; time: string; hero: HeroFace; handlers: BubbleHandlers }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // До загрузки — 4:3: место под фото занято сразу, и лента не прыгает дважды
  const [ratio, setRatio] = useState(4 / 3);
  if (!url || failed) {
    return (
      <div className="bubble-box bubble-box--text">
        <p className="bubble-box__missing">{t("messenger.photo.missing")}</p>
        <Meta time={time} />
      </div>
    );
  }
  return (
    <div className="bubble-box bubble-box--media">
      <button
        type="button"
        className={`photo${loaded ? " photo--loaded" : ""}`}
        style={{ aspectRatio: String(ratio) }}
        onClick={(event) => handlers.onOpenPhoto(url, event.currentTarget)}
        aria-label={`${t("messenger.photo.open")} — ${t("messenger.lightbox", { hero: hero.name })}`}
      >
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={(event) => {
            setRatio(previewRatio(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight));
            setLoaded(true);
          }}
          onError={() => { setFailed(true); handlers.onMediaError(); }}
        />
      </button>
      <Meta time={time} />
    </div>
  );
}

function LocalBubble({ outgoing, time, handlers }: { outgoing: Outgoing; time: string; handlers: BubbleHandlers }) {
  const status = outgoing.status;
  if (outgoing.body.kind === "text") {
    return (
      <div className={`bubble-box bubble-box--text${status === "failed" ? " bubble-box--failed" : ""}`}>
        <p className="bubble-box__text">{outgoing.body.text}</p>
        <Meta time={time} status={status} />
      </div>
    );
  }
  return (
    <div className={`bubble-box bubble-box--voice${status === "failed" ? " bubble-box--failed" : ""}`}>
      <span className="sr-only">{t("messenger.voice.label")}. </span>
      <VoicePlayer url={outgoing.body.localUrl} seed={outgoing.id} durationMs={outgoing.body.durationMs} mine onPlay={handlers.onPlay} />
      <div className="bubble-box__foot">
        <Meta time={time} status={status} />
      </div>
    </div>
  );
}

function FailedLine({ outgoing, handlers }: { outgoing: Outgoing; handlers: BubbleHandlers }) {
  const paywall = outgoing.error?.code === "payment_required";
  return (
    <p className="msg__failed" role="status">
      <span>{paywall ? t("messenger.status.subscribe") : t("messenger.status.failed")}</span>
      {outgoing.error?.retryable !== false && (
        <button type="button" className="msg__action" onClick={() => handlers.onRetry(outgoing.id)}>
          <Icon name="retry" size={14} />
          {t("messenger.retry")}
        </button>
      )}
      <button type="button" className="msg__action msg__action--quiet" onClick={() => handlers.onDiscard(outgoing.id)}>
        {t("messenger.discard")}
      </button>
    </p>
  );
}

export function TypingRow({ kind, hero }: { kind: TypingKind; hero: HeroFace }) {
  const label =
    kind === "recording"
      ? t("messenger.recording")
      : kind === "sending_photo"
        ? t("messenger.sendingPhoto")
        : kind === "sending_video"
          ? t("messenger.sendingVideo")
          : null;
  return (
    <li className="msg msg--hero msg--first msg--last msg--typing">
      <span className="msg__avatar" aria-hidden="true">
        <Avatar src={hero.portraitUrl} name={hero.name} size={30} />
      </span>
      <div className="msg__column">
        <div className="bubble-box bubble-box--typing">
          <span className="sr-only">{t("messenger.typingAria", { hero: hero.name })}</span>
          {kind === "recording" && <Icon name="mic" size={16} />}
          <span className="typing-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {label && (
            <span className="bubble-box__typing-label" aria-hidden="true">
              {label}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

export function DayRow({ label }: { label: string }) {
  return (
    <li className="chat-day">
      <span>{label}</span>
    </li>
  );
}

export function EpisodeRow({ episode, title }: { episode: number; title: string | null }) {
  return (
    <li className="chat-episode">
      <span className="chat-episode__line" aria-hidden="true" />
      <span className="chat-episode__text">
        <span className="chat-episode__label">{t("messenger.episodeStarted", { n: episode })}</span>
        {title && <span className="chat-episode__title">{title}</span>}
      </span>
      <span className="chat-episode__line" aria-hidden="true" />
    </li>
  );
}

export function NoticeRow({ tone, text, time }: { tone: "busy" | "info"; text: string; time: string }) {
  return (
    <li className={`chat-notice chat-notice--${tone}`}>
      <span className="chat-notice__pill">
        {tone === "busy" && <Icon name="clock" size={15} />}
        <span>{text}</span>
        <time className="chat-notice__time">{time}</time>
      </span>
    </li>
  );
}

export function PaywallRow() {
  return (
    <li className="chat-notice chat-notice--paywall">
      <span className="chat-notice__pill">
        <Icon name="lock" size={15} />
        <span>{t("messenger.paywall.line")}</span>
      </span>
    </li>
  );
}
